/**
 * `PublicSkillAssessment` — variante PUBBLICA (non autenticata) del wizard di
 * valutazione skill, per il candidato del flusso di candidatura pubblica.
 *
 * È il pendant di `SkillAssessmentWizard` (area riservata) adattato al contesto
 * pubblico:
 *  - client token-less (`usePublicApiClient`): nessun Bearer, nessun OIDC;
 *  - il profilo è identificato dal Public_Token di rotta
 *    (`/public/submission/:token/valuta`), non dall'utente autenticato;
 *  - mostra da subito **tutte le skill** del catalogo (nessun tastone di scelta
 *    iniziale, nessun filtro "Per business unit"/ruolo, validi solo per gli
 *    utenti interni). Restano il filtro per tipologia e la ricerca testuale;
 *  - il candidato valuta **Livello** ed **Esperienza** (la Propensione
 *    non fa parte del profilo CV esterno);
 *  - salvataggio implicito al cambio di un campo via
 *    `PUT /api/public/public-cv/profiles/:token/evaluations/:skillId`: la scelta
 *    del candidato **sovrascrive** il livello pre-compilato dall'AI. Il Livello
 *    "Nessuno" (`e-0`) rimuove la valutazione.
 *
 * Le valutazioni correnti pre-popolano gli input (endpoint dedicato). Il
 * catalogo skill è servito da endpoint pubblici (`/catalog/skills`).
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompetenceDto,
  ExperienceRange,
  JobDto,
  Livello,
  PublicCvEvaluationDto,
  SkillDto,
  SkillType,
  UpdatePublicCvEvaluationRequest,
} from '../../shared/dto';
import { SKILL_TYPE_LABELS, SKILL_TYPE_VALUES } from '../../shared/dto';
import { usePublicApiClient } from '../../api/usePublicApiClient';
import { ExperienceRangeSelect } from '../../components/ExperienceRangeSelect';
import { LivelloGuide } from '../../components/LivelloGuide';
import { LivelloSelect } from '../../components/LivelloSelect';
import { Alert, Card, Field, PageHeader, TextInput } from '../../components/ui';

/** Filtro per tipologia di skill (secondo select). */
type TypeFilter = 'all' | SkillType | 'none';

/**
 * Filtro di ambito (primo select), adattato al contesto pubblico:
 * - `'rated'`: solo le skill che il candidato ha già valutato;
 * - `'unrated'`: solo le skill ancora senza valutazione;
 * - `'all'`: tutto il catalogo;
 * - `job:<id>`: le skill delle competenze richieste dalla professione scelta.
 * NON esiste la voce "Per business unit" (riservata agli utenti interni).
 */
type ScopeFilter = 'rated' | 'unrated' | 'all' | string;

/** Prefisso del valore di ambito che punta a una professione specifica. */
const JOB_PREFIX = 'job:';

/** Riga della lista: skill del catalogo con l'eventuale valutazione corrente. */
interface SkillRow {
  skillId: string;
  skillName: string;
  description?: string;
  type?: SkillType;
  level: Livello | null;
  experienceRange: ExperienceRange | null;
}

export default function PublicSkillAssessment() {
  const api = usePublicApiClient();
  const queryClient = useQueryClient();
  const { token } = useParams<{ token: string }>();
  const hasToken = token !== undefined && token !== '';
  const encodedToken = encodeURIComponent(token ?? '');

  const skillsKey = ['publicCv', 'catalog', 'skills'] as const;
  const competencesKey = ['publicCv', 'catalog', 'competences'] as const;
  const jobsKey = ['publicCv', 'catalog', 'jobs'] as const;
  const evaluationsKey = ['publicCv', 'evaluations', token] as const;

  // Catalogo pubblico delle skill (intero catalogo, tutte pre-mostrate).
  const skillsQuery = useQuery<SkillDto[]>({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/public/public-cv/catalog/skills'),
  });

  // Competenze e professioni: servono a derivare le skill per il filtro di
  // ambito "Per professione" (una professione → competenze richieste → skill).
  const competencesQuery = useQuery<CompetenceDto[]>({
    queryKey: competencesKey,
    queryFn: () =>
      api.get<CompetenceDto[]>('/api/public/public-cv/catalog/competences'),
  });
  const jobsQuery = useQuery<JobDto[]>({
    queryKey: jobsKey,
    queryFn: () => api.get<JobDto[]>('/api/public/public-cv/catalog/jobs'),
  });

  // Valutazioni correnti del profilo (per pre-popolare gli input).
  const evaluationsQuery = useQuery<PublicCvEvaluationDto[]>({
    queryKey: evaluationsKey,
    queryFn: () =>
      api.get<PublicCvEvaluationDto[]>(
        `/api/public/public-cv/profiles/${encodedToken}/evaluations`,
      ),
    enabled: hasToken,
  });

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  // Ambito: di default "Tutte le skill" (tutto il catalogo pre-mostrato).
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');

  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);
  const competences = useMemo(
    () => competencesQuery.data ?? [],
    [competencesQuery.data],
  );
  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);
  const evaluations = useMemo(
    () => evaluationsQuery.data ?? [],
    [evaluationsQuery.data],
  );

  // skillId → valutazione corrente (livello + esperienza).
  const evaluationBySkill = useMemo(() => {
    const map = new Map<string, PublicCvEvaluationDto>();
    for (const e of evaluations) map.set(e.skillId, e);
    return map;
  }, [evaluations]);

  // Righe complete: tutto il catalogo, con la valutazione corrente se presente.
  const rows = useMemo<SkillRow[]>(() => {
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return skills
      .map((s) => {
        const evaluation = evaluationBySkill.get(s.id);
        return {
          skillId: s.id,
          skillName: s.name,
          ...(s.description !== undefined ? { description: s.description } : {}),
          ...(s.type !== undefined ? { type: s.type } : {}),
          level: evaluation?.level ?? null,
          experienceRange: evaluation?.experienceRange ?? null,
        };
      })
      .sort((a, b) => collator.compare(a.skillName, b.skillName));
  }, [skills, evaluationBySkill]);

  // competenceId → insieme delle sue skill.
  const skillIdsByCompetence = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const c of competences) map.set(c.id, new Set(c.skillIds));
    return map;
  }, [competences]);

  // professione → insieme delle skill (unione delle skill delle competenze
  // richieste dal Job).
  const skillIdsByJob = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const job of jobs) {
      const set = new Set<string>();
      for (const competenceId of job.competenceIds) {
        const skillsOfComp = skillIdsByCompetence.get(competenceId);
        if (skillsOfComp) for (const skillId of skillsOfComp) set.add(skillId);
      }
      map.set(job.id, set);
    }
    return map;
  }, [jobs, skillIdsByCompetence]);

  // Professioni con almeno una competenza, ordinate, per l'optgroup "Per professione".
  const jobOptions = useMemo(() => {
    const collator = new Intl.Collator('it', { sensitivity: 'base' });
    return jobs
      .filter((j) => j.competenceIds.length > 0)
      .map((j) => ({ id: j.id, name: j.name }))
      .sort((a, b) => collator.compare(a.name, b.name));
  }, [jobs]);

  // Filtri: prima l'ambito, poi la tipologia, infine la ricerca testuale.
  const filteredRows = useMemo(() => {
    let result = rows;

    // 1) Ambito.
    if (scopeFilter === 'rated') {
      result = result.filter((r) => r.level !== null);
    } else if (scopeFilter === 'unrated') {
      result = result.filter((r) => r.level === null);
    } else if (scopeFilter.startsWith(JOB_PREFIX)) {
      const allowed = skillIdsByJob.get(scopeFilter.slice(JOB_PREFIX.length));
      result = allowed ? result.filter((r) => allowed.has(r.skillId)) : [];
    }
    // `'all'`: nessun filtro di ambito.

    // 2) Tipologia.
    if (typeFilter !== 'all') {
      result = result.filter((r) =>
        typeFilter === 'none' ? r.type === undefined : r.type === typeFilter,
      );
    }

    // 3) Ricerca testuale.
    const q = search.trim().toLowerCase();
    if (q !== '') {
      result = result.filter((r) => r.skillName.toLowerCase().includes(q));
    }
    return result;
  }, [rows, scopeFilter, typeFilter, search, skillIdsByJob]);

  // Bozze locali: riflettono la scelta prima/durante il salvataggio.
  const [levelDraft, setLevelDraft] = useState<Record<string, Livello>>({});
  const [yearsDraft, setYearsDraft] = useState<Record<string, ExperienceRange>>({});

  const currentLevel = (skillId: string): Livello | null =>
    levelDraft[skillId] ?? evaluationBySkill.get(skillId)?.level ?? null;
  const currentYears = (skillId: string): ExperienceRange | null =>
    yearsDraft[skillId] ?? evaluationBySkill.get(skillId)?.experienceRange ?? null;

  // Salvataggio di una singola skill (upsert/rimozione via PUT pubblico).
  const saveEvaluation = useMutation({
    mutationFn: async ({
      skillId,
      level,
      experienceRange,
    }: {
      skillId: string;
      level: Livello;
      experienceRange: ExperienceRange | null;
    }) => {
      const body: UpdatePublicCvEvaluationRequest = { level, experienceRange };
      return api.put<PublicCvEvaluationDto[]>(
        `/api/public/public-cv/profiles/${encodedToken}/evaluations/${encodeURIComponent(skillId)}`,
        body,
      );
    },
    onSuccess: (updated, variables) => {
      // Aggiorna la cache delle valutazioni con l'elenco restituito e pulisce le
      // bozze della skill salvata.
      queryClient.setQueryData(evaluationsKey, updated);
      setLevelDraft(({ [variables.skillId]: _l, ...rest }) => rest);
      setYearsDraft(({ [variables.skillId]: _y, ...rest }) => rest);
    },
  });

  const savingSkillId = saveEvaluation.isPending
    ? saveEvaluation.variables?.skillId ?? null
    : null;

  // Cambio del Livello: aggiorna la bozza e salva subito con l'esperienza
  // corrente. Con "Nessuno" (e-0) l'esperienza non ha senso: si azzera.
  const handleLevelChange = (skillId: string, level: Livello) => {
    setLevelDraft((prev) => ({ ...prev, [skillId]: level }));
    const years = level === 'e-0' ? null : currentYears(skillId);
    if (level === 'e-0') {
      setYearsDraft(({ [skillId]: _y, ...rest }) => rest);
    }
    saveEvaluation.mutate({ skillId, level, experienceRange: years });
  };

  // Cambio dell'esperienza: valido solo se il Livello è già impostato (>= e-1).
  const handleYearsChange = (skillId: string, years: ExperienceRange | null) => {
    const level = currentLevel(skillId);
    if (level === null || level === 'e-0') return;
    if (years !== null) {
      setYearsDraft((prev) => ({ ...prev, [skillId]: years }));
    }
    saveEvaluation.mutate({ skillId, level, experienceRange: years });
  };

  const isLoading =
    skillsQuery.isLoading ||
    evaluationsQuery.isLoading ||
    competencesQuery.isLoading ||
    jobsQuery.isLoading;

  if (!hasToken) {
    return (
      <section aria-labelledby="public-skill-assessment-title">
        <PageHeader title="Link non valido" titleId="public-skill-assessment-title" />
        <Alert tone="error">
          Il link alla valutazione non è valido. Torna alla pagina del tuo profilo.
        </Alert>
      </section>
    );
  }

  return (
    <section
      className="assessment-wizard skill-assessment-wizard"
      aria-labelledby="public-skill-assessment-title">
      <PageHeader
        title="Valuta le tue skill"
        titleId="public-skill-assessment-title"
        subtitle="Scegli il Livello per salvare; l'Esperienza è facoltativa. Le tue scelte aggiornano il profilo."
        actions={
          <Link
            to={`/public/submission/${encodedToken}`}
            className="ui-btn ui-btn--ghost"
          >
            ← Torna al profilo
          </Link>
        }
      />

      {(skillsQuery.isError || evaluationsQuery.isError) && (
        <Alert tone="error">Impossibile caricare le skill. Riprova più tardi.</Alert>
      )}
      {saveEvaluation.isError && (
        <Alert tone="error">Salvataggio non riuscito. Riprova.</Alert>
      )}

      <Field
        id="public-skill-search"
        label="Cerca skill"
        hint="Filtra per ambito e tipologia; la ricerca opera nella selezione corrente."
      >
        <div className="competence-search">
          {/* Filtro di ambito: generale + una voce per professione. Nessuna
              voce "Per business unit" (riservata agli utenti interni). */}
          <span className="competence-search__department user-admin__select">
            <select
              id="public-skill-scope"
              aria-label="Filtra le skill per ambito"
              value={scopeFilter}
              disabled={skillsQuery.isLoading}
              onChange={(e) => setScopeFilter(e.target.value)}
            >
              <optgroup label="Generale">
                <option value="all">Tutte le skill</option>
                <option value="unrated">Non valutate</option>
                <option value="rated">Già valutate</option>
              </optgroup>
              {jobOptions.length > 0 && (
                <optgroup label="Per professione">
                  {jobOptions.map((j) => (
                    <option key={j.id} value={`${JOB_PREFIX}${j.id}`}>
                      {j.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </span>

          {/* Filtro per tipologia di skill. */}
          <span className="competence-search__department user-admin__select">
            <select
              id="public-skill-type"
              aria-label="Filtra le skill per tipologia"
              value={typeFilter}
              disabled={skillsQuery.isLoading}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            >
              <option value="all">Tutte le tipologie</option>
              {SKILL_TYPE_VALUES.map((t) => (
                <option key={t} value={t}>
                  {SKILL_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </span>

          <TextInput
            id="public-skill-search-input"
            className="competence-search__input"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca una skill…"
            autoComplete="off"
          />
        </div>
      </Field>

      <Card className="assessment-wizard__skills-card">
        <div className="assessment-wizard__skills-header">
          <h3 className="assessment-wizard__skills-title">Skills</h3>
          <LivelloGuide />
          <div
            className="assessment-wizard__inputs assessment-wizard__col-headers"
            aria-hidden="true"
          >
            <span className="assessment-wizard__col-header skill-list__picker-level">
              Livello
            </span>
            <span className="assessment-wizard__col-header skill-list__picker-years">
              Esperienza
            </span>
          </div>
        </div>

        {isLoading ? (
          <p role="status">Caricamento delle skill…</p>
        ) : filteredRows.length === 0 ? (
          <p role="status">Nessuna skill corrisponde ai filtri o alla ricerca.</p>
        ) : (
          <ul className="assessment-wizard__skills">
            {filteredRows.map((row) => {
              const isSaving = savingSkillId === row.skillId;
              const level = currentLevel(row.skillId);
              const yearsValue = level === 'e-0' ? null : currentYears(row.skillId);
              return (
                <li key={row.skillId} className="assessment-wizard__step">
                  <span className="assessment-wizard__skill-label assessment-wizard__skill-label--stacked">
                    <span className="assessment-wizard__skill-name assessment-wizard__skill-name--sm">
                      {row.skillName}
                    </span>
                    {row.description && (
                      <span className="assessment-wizard__skill-desc">
                        {row.description}
                      </span>
                    )}
                  </span>
                  <div className="assessment-wizard__inputs">
                    <span
                      className="user-admin__select skill-list__picker-level"
                      data-empty={level === null || undefined}
                    >
                      {/* `onClear` rende selezionabile "Nessuno" (e-0), che
                          rimuove la valutazione lato server. */}
                      <LivelloSelect
                        label={`Livello per ${row.skillName}`}
                        value={level}
                        disabled={isSaving}
                        onChange={(next) => handleLevelChange(row.skillId, next)}
                      />
                    </span>
                    <span
                      className="user-admin__select skill-list__picker-years"
                      data-empty={yearsValue === null || undefined}
                    >
                      <ExperienceRangeSelect
                        label={`Anni di esperienza per ${row.skillName}`}
                        value={yearsValue}
                        disabled={isSaving || level === null || level === 'e-0'}
                        onChange={(next) => handleYearsChange(row.skillId, next)}
                      />
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </section>
  );
}
